import { useQuery } from '@vue/apollo-composable';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import {
  AI_ASSISTANT_SCHOOL_FLAGS,
  AI_ASSISTANT_STUDENT_FLAGS,
  type AiAssistantSchoolFlagsResult,
  type AiAssistantStudentFlagsResult,
} from '@/graphql/aiAssistant';

/**
 * Gating for the AI Assistant surface (achievable E1).
 *
 * - Admin/staff entry renders only when `aiAssistantEnabled` (master flag)
 *   AND the staff member holds the ai_assistant.view permission grant.
 * - Student entry renders only when `aiAssistantEnabled && aiStudentAccess`,
 *   and never on CBT-only student portals (which collapse the whole nav).
 *
 * The flags come from the school/config GraphQL surface; the server enforces
 * the same matrix on POST /api/ai/chat (D4), so this is presentation-only
 * gating — hiding entries, not security.
 */
export function useAiAssistantAccess() {
  const userStore = useUserStore();

  // Same cast the router guard uses: the generated `me` union doesn't carry
  // __typename on every branch even though the wire always sends it.
  const role = computed(() => (userStore.currentUser as { __typename?: string } | null)?.__typename);
  const isStudent = computed(() => role.value === 'Student');
  const isStaff = computed(() => role.value === 'User');

  // Students cannot call getSchool, staff shouldn't pay for the student-shaped
  // query — run exactly one of the two documents based on role.
  const {
    result: schoolResult,
    loading: loadingSchool,
    refetch: refetchSchool,
  } = useQuery<AiAssistantSchoolFlagsResult>(
    AI_ASSISTANT_SCHOOL_FLAGS,
    null,
    () => ({ enabled: isStaff.value }),
  );

  const {
    result: studentResult,
    loading: loadingStudent,
  } = useQuery<AiAssistantStudentFlagsResult>(
    AI_ASSISTANT_STUDENT_FLAGS,
    null,
    () => ({ enabled: isStudent.value }),
  );

  const flags = computed(() => {
    if (isStudent.value) {
      const school = studentResult.value?.me?.school;
      return {
        aiAssistantEnabled: !!school?.aiAssistantEnabled,
        aiStudentAccess: !!school?.aiStudentAccess,
      };
    }
    const school = schoolResult.value?.getSchool;
    return {
      aiAssistantEnabled: !!school?.aiAssistantEnabled,
      aiStudentAccess: !!school?.aiStudentAccess,
    };
  });

  const aiAssistantEnabled = computed(() => flags.value.aiAssistantEnabled);
  const aiStudentAccess = computed(() => flags.value.aiStudentAccess);

  /** Admin/staff nav entry: master flag + the ai_assistant.view grant. */
  const showAdminEntry = computed(
    () =>
      isStaff.value &&
      aiAssistantEnabled.value &&
      can(userStore.currentUser, AppPermissions.AiAssistant.Actions.View, AppPermissions.AiAssistant.Resource),
  );

  /** Student nav entry: both flags, and never on a CBT-only portal (E1). */
  const showStudentEntry = computed(
    () =>
      isStudent.value &&
      aiAssistantEnabled.value &&
      aiStudentAccess.value &&
      !userStore.isCbtOnlyStudentPortal,
  );

  /** Whether the CURRENT user may use the chat surface at all. */
  const canUseAssistant = computed(() => (isStudent.value ? showStudentEntry.value : showAdminEntry.value));

  const loading = computed(() => (isStudent.value ? loadingStudent.value : loadingSchool.value));

  return {
    aiAssistantEnabled,
    aiStudentAccess,
    showAdminEntry,
    showStudentEntry,
    canUseAssistant,
    isStudent,
    loading,
    refetchFlags: refetchSchool,
  };
}
